import React, { useState } from "react";
import { getOrder, updateOrder } from "../../lib/api";
import { useLoaderData, useNavigate } from "react-router";
import { IOrder } from "../../types/api";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import {
    capitalize,
    currencyFormat,
    defaultDateTimeFormat,
} from "../../lib/utils";
import Select from "react-select";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { alertActions } from "../../features/alert/alertSlice";
import { STRIPE_LINK } from "../../lib/globals";
import ProductDetails from "../../components/ProductDetails";

export const loader = async ({ params }: any) => {
    const { orderId } = params;
    const [{ order }] = await Promise.all([getOrder(orderId)]);

    return {
        orderId,
        order,
        header: `Order #${orderId}`,
    };
};

const Order = (): React.JSX.Element => {
    const { order: loadedOrder } = useLoaderData() as { order: IOrder };
    const [order, setOrder] = useState<IOrder>(loadedOrder);
    const [orderStatus, setOrderStatus] = useState<IOrder["status"]>(
        order.status,
    );
    const [orderNote, setOrderNote] = useState<string>();
    const dispatch = useAppDispatch();

    const orderStatuses: IOrder["status"][] = [
        "pending",
        "processing",
        "completed",
        "cancelled",
    ];

    const handleSubmit = async () => {
        await updateOrder(
            order.id,
            orderStatus,
            orderNote?.trim() !== "" ? orderNote : undefined,
        )
            .then(response => {
                setOrder(response.order);
                dispatch(
                    alertActions.success({
                        title: `Order updated successfully`,
                        dismissible: true,
                    }),
                );
                setOrderNote(undefined);
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem updating the order.`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    return (
        <section className="orderContent" data-testid="orderContent">
            <Row>
                <Col xs={12} lg={9}>
                    <h3>Order Details</h3>
                    <Row className="orderDetails">
                        <Col xs={6} sm={3}>
                            <h5>Order Status:</h5>
                        </Col>
                        <Col xs={6} sm={3}>
                            <Select
                                isSearchable={false}
                                isClearable={false}
                                options={orderStatuses.map(s => ({
                                    value: s,
                                    label: capitalize(s),
                                }))}
                                defaultValue={{
                                    value: capitalize(order.status),
                                    label: capitalize(order.status),
                                }}
                                onChange={e => {
                                    setOrderStatus(
                                        e!.value as IOrder["status"],
                                    );
                                }}
                            />
                        </Col>
                        <Col xs={6} sm={3}>
                            <h5>Order Date:</h5>
                        </Col>
                        <Col xs={6} sm={3}>
                            {defaultDateTimeFormat.format(new Date(order.date))}
                        </Col>
                        <Col xs={6} sm={3}>
                            <h5>Name:</h5>
                        </Col>
                        <Col xs={6} sm={3}>
                            {order.user.name}
                        </Col>
                        <Col xs={6} sm={3}>
                            <h5>Email:</h5>
                        </Col>
                        <Col xs={6} sm={3} className="email">
                            {order.user.email}
                        </Col>

                        {order.status === "completed" && (
                            <>
                                <Col xs={6} sm={3}>
                                    <h5>Order Completion Date:</h5>
                                </Col>
                                <Col xs={6} sm={3}>
                                    {order.completionDate &&
                                        defaultDateTimeFormat.format(
                                            new Date(order.completionDate),
                                        )}
                                </Col>
                                <Col xs={6} sm={3}>
                                    <h5>Phone:</h5>
                                </Col>
                                <Col xs={6} sm={3}>
                                    {order.user.phone}
                                </Col>
                            </>
                        )}

                        <Col xs={6} sm={3}>
                            <h5>Payment Type:</h5>
                        </Col>
                        <Col xs={6} sm={3}>
                            {order.paymentType}
                        </Col>

                        <Col xs={6} sm={3}>
                            <h5>Order Total:</h5>
                        </Col>
                        <Col xs={6} sm={3}>
                            {currencyFormat.format(order.total)}
                        </Col>

                        {order.coupon && (
                            <>
                                <Col xs={6} sm={3}>
                                    <h5>Coupon Used:</h5>
                                </Col>
                                <Col xs={6} sm={3}>
                                    {order.coupon.code}
                                </Col>
                                <Col xs={6} sm={3}>
                                    <h5>Discount Total:</h5>
                                </Col>
                                <Col xs={6} sm={3}>
                                    {currencyFormat.format(order.discount)}
                                </Col>
                            </>
                        )}
                        {order.paymentType === "Credit Card" && (
                            <>
                                <Col xs={6} sm={3}>
                                    <h5>Stripe Link:</h5>
                                </Col>
                                <Col xs={6} sm={9} className="stripeLink">
                                    <a
                                        href={`${STRIPE_LINK}${order.paymentDetails?.paymentIntentId}`}
                                        target="_blank"
                                    >{`${STRIPE_LINK}${order.paymentDetails?.paymentIntentId}`}</a>
                                </Col>
                            </>
                        )}

                        <Col xs={3}>
                            <Button variant="primary" onClick={handleSubmit}>
                                Update Order
                            </Button>
                        </Col>

                        <Row className="mt-4">
                            <Col>
                                <h3>Product Details</h3>
                                {order.products
                                    ? order.products.map((p, idx) => (
                                          <ProductDetails
                                              product={p}
                                              key={idx}
                                          />
                                      ))
                                    : ""}
                            </Col>
                        </Row>
                    </Row>
                </Col>
                <Col lg={3} className="mt-4 mt-lg-0">
                    <h3>Order History</h3>
                    <div className="orderHistory">
                        {order.history !== null &&
                            order.history.map((h, idx) => {
                                return (
                                    <div key={idx}>
                                        <cite>
                                            {defaultDateTimeFormat.format(
                                                new Date(h.date),
                                            )}
                                        </cite>
                                        <blockquote>
                                            {h.message}
                                            <br />
                                        </blockquote>
                                    </div>
                                );
                            })}
                        <Form.Control
                            as="textarea"
                            rows={3}
                            className="newNote"
                            placeholder="Add new order note..."
                            value={orderNote || ""}
                            onChange={e => setOrderNote(e.currentTarget.value)}
                        />
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default Order;
